import { Show, SignInButton, SignUpButton } from '@clerk/react'
import { Theme } from '@radix-ui/themes'
import { BookOpen, Briefcase, LayoutDashboard, Shield } from 'lucide-react'
import { Suspense, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { ErrorBoundary } from 'react-error-boundary'
import { Toaster } from 'react-hot-toast'
import { Link, useLocation } from '#router'
import { Button } from '@/components/ui/button'
import { StreakCounter } from '@/components/ui/streak-counter'
import { useChrome } from '@/hooks/use-chrome'
import { TitleProvider } from '@/hooks/use-document-title'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { DeviceProvider, useIsMobile } from '@/hooks/use-mobile'
import type { DeviceType } from '@/lib/device'
import { resolveDeviceType, setDeviceCookie } from '@/lib/device'
import { cn } from '@/lib/utils'
import { useIsAdmin } from './admin-protected-route'
import ErrorFallback from './error-fallback'
import { FeaturebaseProvider } from './featurebase-provider'
import LoadingFallback from './loading-fallback'
import { MobileNav } from './mobile-nav'
import { RouteSwitch } from './routes'
import { StreakProvider } from './streak-context'
import { ThemeProvider } from './theme-provider'
import ThemeToggle from './theme-toggle'
import { ThemedUserButton } from './themed-user-button'

const NAV_V2_PLACEHOLDERS = [
  { label: 'Courses', icon: BookOpen },
  { label: 'Job Search', icon: Briefcase },
] as const

const TOASTER_Z_INDEX = 2147483647

function GlobalToaster() {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted) return null

  return createPortal(
    <Toaster
      containerClassName="!z-[2147483647]"
      containerStyle={{ zIndex: TOASTER_Z_INDEX }}
      position="top-right"
      toastOptions={{
        duration: 4000,
        style: {
          zIndex: TOASTER_Z_INDEX,
          background: 'var(--background)',
          color: 'var(--foreground)',
          border: '1px solid var(--border)',
        },
      }}
    />,
    document.body,
  )
}

function AppHeader() {
  const isMobile = useIsMobile()
  const location = useLocation()
  const currentPathWithSearch = `${location.pathname}${location.search}`
  const normalizedPath = location.pathname.split('?')[0]

  const isActive = (path: string) => {
    if (path === '/') return normalizedPath === '/'
    return normalizedPath === path || normalizedPath.startsWith(`${path}/`)
  }

  const chrome = useChrome()

  const { isAdmin, isLoaded: isAdminLoaded } = useIsAdmin()
  const clientDashboard = useFeatureFlag('enable-client-dashboard')
  const navV2 = useFeatureFlag('enable-nav-v2')
  const interviewingioFlag = useFeatureFlag('enable-interviewingio', {
    skipAdminBypass: true,
  })

  // Lazy-load Rajdhani font only when interviewing.io button is shown
  useEffect(() => {
    if (!interviewingioFlag.enabled) return
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href =
      'https://fonts.googleapis.com/css2?family=Rajdhani:wght@500;600&display=swap'
    document.head.appendChild(link)
    return () => {
      link.remove()
    }
  }, [interviewingioFlag.enabled])

  if (isMobile) return <MobileNav />

  return (
    <header
      className={cn(
        'bg-background/95 supports-[backdrop-filter]:bg-background/60 top-0 z-50 w-full overflow-x-hidden border-b backdrop-blur dark:border-transparent',
        chrome.sticky && 'sticky',
      )}
    >
      <div
        className={cn(
          'flex h-16 items-center justify-between',
          chrome.shell === 'page' ? 'page-shell' : 'container mx-auto px-4',
        )}
      >
        <div className="flex items-center gap-2">
          <Link
            href="/"
            aria-label="Beyond CTCI"
            className="flex items-center space-x-2"
          >
            <img
              src="/icons/bctci.webp"
              alt=""
              aria-hidden="true"
              className="h-8 w-8"
            />
            <span className="hidden text-lg font-bold sm:inline-block">
              Beyond CTCI
            </span>
          </Link>
        </div>

        <nav className="flex items-center gap-1">
          {navV2.isLoaded &&
            navV2.enabled &&
            clientDashboard.isLoaded &&
            clientDashboard.enabled && (
              <Show when="signed-in">
                <Button
                  asChild
                  variant={isActive('/dashboard') ? 'secondary' : 'ghost'}
                >
                  <Link href="/dashboard">
                    <LayoutDashboard className="mr-1 h-4 w-4" />
                    Dashboard
                  </Link>
                </Button>
                {NAV_V2_PLACEHOLDERS.map(({ label, icon: Icon }) => (
                  <Button
                    key={label}
                    variant="ghost"
                    disabled
                    title="Coming soon"
                    className="cursor-default opacity-50"
                  >
                    <Icon className="mr-1 h-4 w-4" />
                    {label}
                  </Button>
                ))}
              </Show>
            )}
          <Button
            asChild
            variant={isActive('/problems') ? 'secondary' : 'ghost'}
          >
            <Link href="/problems">Problems</Link>
          </Button>
          {interviewingioFlag.isLoaded && interviewingioFlag.enabled && (
            <a
              href="https://start.interviewing.io/beyond-ctci/part-i-preface/what-is-this-book"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-9 items-center gap-1 rounded-md px-3 text-base font-bold leading-none text-black dark:text-white transition-colors hover:bg-accent"
            >
              <img
                src="/icons/interviewing-io.webp"
                alt="interviewing.io"
                className="h-5 w-auto"
              />
              <span style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                interviewing.io
              </span>
            </a>
          )}
          <Show when="signed-in">
            {isAdminLoaded && isAdmin && (
              <Button
                asChild
                variant={isActive('/admin') ? 'secondary' : 'ghost'}
                size="sm"
              >
                <Link href="/admin">
                  <Shield className="mr-1 h-4 w-4" />
                  Admin
                </Link>
              </Button>
            )}
          </Show>
          <StreakCounter />
          <ThemeToggle />
          <Show when="signed-out">
            <SignInButton
              mode="modal"
              forceRedirectUrl={currentPathWithSearch}
              fallbackRedirectUrl={currentPathWithSearch}
            >
              <Button variant="ghost">Sign In</Button>
            </SignInButton>
            <SignUpButton
              mode="modal"
              forceRedirectUrl={currentPathWithSearch}
              fallbackRedirectUrl={currentPathWithSearch}
            >
              <Button variant="default">Sign Up</Button>
            </SignUpButton>
          </Show>
          <Show when="signed-in">
            <ThemedUserButton />
          </Show>
        </nav>
      </div>
    </header>
  )
}

function App({ deviceType }: { deviceType?: DeviceType }) {
  // Resolve the real device type: SSR prop → window global → client-side detection.
  // On the client, resolveDeviceType may refine the server's guess (e.g. iPadOS
  // detected via maxTouchPoints). When that happens, persist the correct type in a
  // cookie so subsequent SSR requests render the right layout on the first paint.
  const serverType =
    typeof window !== 'undefined'
      ? window.__DEVICE_TYPE__ || 'desktop'
      : 'desktop'
  const resolvedDeviceType = deviceType ?? resolveDeviceType(serverType)

  useEffect(() => {
    if (resolvedDeviceType !== serverType) {
      setDeviceCookie(resolvedDeviceType)
    }
  }, [resolvedDeviceType, serverType])

  return (
    <ThemeProvider defaultTheme="system" storageKey="beyond-ctci-theme">
      <FeaturebaseProvider>
        <Theme>
          <DeviceProvider deviceType={resolvedDeviceType}>
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <TitleProvider>
                <StreakProvider>
                  <div className="min-h-screen max-w-screen">
                    <AppHeader />

                    <Suspense fallback={<LoadingFallback />}>
                      <RouteSwitch />
                    </Suspense>
                  </div>
                </StreakProvider>
              </TitleProvider>
            </ErrorBoundary>
            <GlobalToaster />
          </DeviceProvider>
        </Theme>
      </FeaturebaseProvider>
    </ThemeProvider>
  )
}

export default App
